import { mapToOptions } from './utils/filterSearchResults';
import { CustomFilterGroups, FilterGroupTypes, FilterSummaryGroups } from './types';
import type { CheckboxFilterGroup, FilterGroup, InputFilterGroup } from './types';

export const MSRP = 'MSRP';
export const SELLING_PRICE = 'Selling Price';
export const COST = 'Cost';
export const ODOMETER = 'Odometer';
export const MAX_VISIBLE_FILTER_ITEMS = 3;
export const MAX_SELECTED_FILTER_ITEMS = 20;
// Maximum number of active scenarios (defined locally — cannot import DRAFT_DEAL_LIMIT from
// activities-desking; that would be a circular dependency). Confirmed by PO as 3 (AC-6.3).
export const MAX_SCENARIO_COUNT = 3;
export const MAX_MOBILE_WIDTH_PX = 750;
export const DEFAULT_CARD_INDEX = 0;
export const RANK_INDEX_OFFSET = 1;
export const NOT_IN_STOCK_ALERT_MESSAGE =
    "We've found a Match to this VIN number, however this Vehicle is not in your Dealership's Inventory. Proceed accordingly.";

// AI Search Constants
export enum AI_PROMPT_MODE {
    SEMANTIC_SEARCH = 'semantic_search',
    SIMILARITY_SEARCH = 'similarity_search',
    DEFAULT = 'default'
}

export enum AI_PROMPT_PLACEHOLDER {
    ALL_VEHICLES = 'all vehicles',
    SEARCH = 'search'
}

export const INVENTORY_FALLBACK_BANNER_TEXT = 'No similar vehicles found. Showing all inventory';

export enum AI_REQUEST_TRIGGER {
    DRAWER_OPEN = 'drawerOpen',
    USER_SEARCH = 'userSearch',
    SEARCH_CLEAR = 'searchClear',
    FILTER_CHANGE = 'filterChange',
    FILTERS_CLEAR = 'filtersClear',
    SORT_CHANGE = 'sortChange',
    PAGINATION = 'pagination',
    INVENTORY_FALLBACK = 'inventoryFallback'
}

export enum AI_PROMPT_KIND {
    SIMILARITY = 'similarity',
    ALL_VEHICLES = 'allVehicles',
    PLACEHOLDER = 'placeholder',
    VIN_LIKE = 'vinLike',
    FREE_TEXT = 'freeText'
}

export const initialFilterList: FilterGroup[] = [
    {
        id: FilterSummaryGroups.INVENTORY_TYPES,
        title: 'Inventory Type',
        type: FilterGroupTypes.CHECKBOX,
        items: []
    },
    { id: FilterSummaryGroups.YEARS, title: 'Year', type: FilterGroupTypes.CHECKBOX, items: [], settings: { reverse: true } },
    { id: FilterSummaryGroups.MAKES, title: 'Make', type: FilterGroupTypes.CHECKBOX, items: [] },
    {
        id: FilterSummaryGroups.MODELS,
        title: 'Model',
        type: FilterGroupTypes.CHECKBOX,
        items: []
    },
    { id: FilterSummaryGroups.TRIMS, title: 'Trim', type: FilterGroupTypes.CHECKBOX, items: [] },
    {
        id: FilterSummaryGroups.BODY_STYLES,
        title: 'Body Style',
        type: FilterGroupTypes.CHECKBOX,
        items: []
    },
    {
        id: CustomFilterGroups.PRICE,
        title: 'Price',
        type: FilterGroupTypes.INPUT,
        prefix: '$',
        items: [
            { label: 'Min', value: '', queryParameter: 'minPrice' },
            { label: 'Max', value: '', queryParameter: 'maxPrice' }
        ]
    },
    {
        id: CustomFilterGroups.MILEAGE,
        title: 'Mileage',
        type: FilterGroupTypes.INPUT,
        suffix: 'mi',
        items: [
            { label: 'Min', value: '', queryParameter: 'minOdometer' },
            { label: 'Max', value: '', queryParameter: 'maxOdometer' }
        ]
    },
    {
        id: FilterSummaryGroups.ENGINES,
        title: 'Engine',
        type: FilterGroupTypes.CHECKBOX,
        items: []
    },
    {
        id: FilterSummaryGroups.EXTCOLORS,
        title: 'Exterior Color',
        type: FilterGroupTypes.CHECKBOX,
        items: []
    },
    {
        id: FilterSummaryGroups.INTCOLORS,
        title: 'Interior Color',
        type: FilterGroupTypes.CHECKBOX,
        items: []
    }
];

export const initialDealershipsFilterGroup: CheckboxFilterGroup = {
    id: CustomFilterGroups.DEALERSHIPS,
    title: 'Dealerships',
    type: FilterGroupTypes.CHECKBOX,
    items: []
};

export const initialAgeOfInventoryFilterGroup: InputFilterGroup = {
    id: CustomFilterGroups.AGE,
    title: 'Age of Inventory',
    type: FilterGroupTypes.INPUT,
    items: [
        { label: 'Min Days', value: '', queryParameter: 'minAgeDays' },
        { label: 'Max Days', value: '', queryParameter: 'maxAgeDays' }
    ]
};

export const SortOptionLabel = {
    yearDesc: 'Year: New to Old',
    yearAsc: 'Year: Old to New',
    inventoryTypeAsc: 'Inventory: New to Old',
    inventoryTypeDesc: 'Inventory: Old to New',
    ageAsc: 'Age of Inventory: New to Old',
    ageDesc: 'Age of Inventory: Old to New',
    odometerAsc: 'Mileage: Low to High',
    odometerDesc: 'Mileage: High to Low',
    listedPriceAsc: 'Selling Price: Low to High',
    listedPriceDesc: 'Selling Price: High to Low'
} as const;

export const AISortOptionLabel = {
    default: 'Default',
    ...SortOptionLabel
} as const;

export const sortByOptions = mapToOptions(SortOptionLabel);
export const aiSortByOptions = mapToOptions(AISortOptionLabel);
